/* @jsx h */
import { defineComponent, h } from "vue";
import { cn, useLocalization } from "@lemmonhq/uikit";
import type { ServiceSchedule } from "@lemmonhq/types";

// Matches the weekday keys `filterServicesBySchedule` reads via
// `now.toLocaleDateString("en-US", { weekday: "short" })`.
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export type ServiceDaySelectProps = {
  value: ServiceSchedule;
  onChange: (schedule: ServiceSchedule) => void;
};

export const ServiceDaySelect = defineComponent<ServiceDaySelectProps>({
  name: "ServiceDaySelect",
  props: ["value", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const addTimeRange = (day: string) => {
      const currentRanges = props.value[day] || [];
      props.onChange({
        ...props.value,
        [day]: [...currentRanges, { start: "09:00", end: "17:00" }],
      });
    };

    const removeTimeRange = (day: string, index: number) => {
      const newRanges = [...(props.value[day] || [])];
      newRanges.splice(index, 1);
      const newSchedule = { ...props.value, [day]: newRanges };
      if (newRanges.length === 0) {
        delete newSchedule[day];
      }
      props.onChange(newSchedule);
    };

    const updateTimeRange = (
      day: string,
      index: number,
      field: "start" | "end",
      value: string,
    ) => {
      const newRanges = [...(props.value[day] || [])];
      newRanges[index] = { ...newRanges[index], [field]: value };
      props.onChange({ ...props.value, [day]: newRanges });
    };

    return () => (
      <div class="space-y-1">
        {DAYS.map((day) => (
          <div key={day} class="card border bg-base-200 border-base-300 p-0">
            <div class="card-body px-4 py-2">
              <div class="flex items-center justify-between p-0">
                <h4
                  class={cn(
                    "card-title text-sm uppercase font-normal",
                    Boolean(props.value[day]) && "text-accent font-semibold",
                  )}
                >
                  {day}
                </h4>
                <button
                  type="button"
                  class="btn btn-xs btn-secondary text-center"
                  onClick={() => addTimeRange(day)}
                >
                  <span class="text-sm">+</span>
                </button>
              </div>

              <div class="space-y-1">
                {(props.value[day] || []).map((range, index) => (
                  <div
                    key={index}
                    class="grid grid-cols-2 gap-3 lg:grid-cols-[repeat(2,minmax(0,1fr))_auto] lg:items-end"
                  >
                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.start_time")}
                      </span>
                      <input
                        type="time"
                        value={range.start}
                        onChange={(e) =>
                          updateTimeRange(
                            day,
                            index,
                            "start",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.end_time")}
                      </span>
                      <input
                        type="time"
                        value={range.end}
                        onChange={(e) =>
                          updateTimeRange(
                            day,
                            index,
                            "end",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <button
                      type="button"
                      class="btn btn-sm btn-secondary self-end justify-self-end"
                      onClick={() => removeTimeRange(day, index)}
                    >
                      <span class="material-symbols-outlined text-error">
                        delete
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  },
});
